/**
 * Языки Aptile: язык интерфейса ученика и языки, которые он изучает.
 *
 * Один и тот же набор кодов служит обеим ролям: в бете русскоязычный
 * ученик учит английский; остальные языки видны как «Скоро». Изучаемых
 * языков у ученика сколько угодно; у каждого свои ступень, план урока,
 * карман и прогресс. Запрос называет язык заголовком
 * `X-Learning-Language`, без него сервер берёт активный язык ученика.
 */

import { z } from 'zod';

import type { CurriculumLevel } from './curriculum';

export const LANGUAGE_CODES = ['ru', 'en', 'zh', 'de', 'fr', 'es'] as const;
export type LanguageCode = (typeof LANGUAGE_CODES)[number];
export const LanguageCodeSchema = z.enum(LANGUAGE_CODES);

/** Самоназвание языка — так его ищут в списке, на каком бы языке ни был интерфейс. */
export const LANGUAGE_SELF_NAMES: Readonly<Record<LanguageCode, string>> = {
  ru: 'Русский',
  en: 'English',
  zh: '中文',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
};

/** Английские названия языков — для промптов модели (она пишет инструкции по-английски). */
export const LANGUAGE_ENGLISH_NAMES: Readonly<Record<LanguageCode, string>> = {
  ru: 'Russian',
  en: 'English',
  zh: 'Mandarin Chinese (simplified characters)',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
};

/** Флаг-эмодзи для переключателя изучаемого языка. */
export const LANGUAGE_FLAGS: Readonly<Record<LanguageCode, string>> = {
  ru: '🇷🇺',
  en: '🇬🇧',
  zh: '🇨🇳',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
};

/**
 * Языки интерфейса — те, для которых есть словарь интерфейса. Изучать
 * можно больше (испанский), а интерфейс появится с переводом словаря.
 */
export const UI_LANGUAGES = ['ru', 'en', 'zh', 'de', 'fr'] as const satisfies readonly LanguageCode[];
export type UiLanguageCode = (typeof UI_LANGUAGES)[number];
export const UiLanguageCodeSchema = z.enum(UI_LANGUAGES);

export function isUiLanguage(language: LanguageCode): language is UiLanguageCode {
  return (UI_LANGUAGES as readonly LanguageCode[]).includes(language);
}

/** Язык интерфейса по умолчанию — аудитория беты русскоязычная. */
export const DEFAULT_UI_LANGUAGE: LanguageCode = 'ru';
/** Изучаемый язык по умолчанию — для него готова программа. */
export const DEFAULT_TARGET_LANGUAGE: LanguageCode = 'en';

/**
 * Для каких изучаемых языков уже есть программа уроков. В бете программа
 * только английская: немецкая лежит в коде, но выключена — язык показывается
 * как «Скоро» и не генерируется (`seedCurriculum.ts`).
 */
export const CURRICULUM_LANGUAGES: readonly LanguageCode[] = ['en'];

/**
 * Курсы, открытые ученикам в бете. Остальные языки в списке видны с
 * пометкой «Скоро» и не добавляются (сервер отвечает `LANGUAGE_COMING_SOON`).
 */
export const OPEN_LANGUAGES: readonly LanguageCode[] = ['en'];

export function isOpenLanguage(language: LanguageCode): boolean {
  return OPEN_LANGUAGES.includes(language);
}

export function hasCurriculum(language: LanguageCode): boolean {
  return CURRICULUM_LANGUAGES.includes(language);
}

/** Код из произвольной строки (`navigator.language`, заголовок): `de-AT` → `de`. */
export function toLanguageCode(raw: string | null | undefined): LanguageCode | null {
  const base = raw?.trim().toLowerCase().split(/[-_]/)[0];
  return LANGUAGE_CODES.find((code) => code === base) ?? null;
}

/** Заголовок запроса с изучаемым языком, к которому относятся данные. */
export const LEARNING_LANGUAGE_HEADER = 'X-Learning-Language';

/** Изучаемый язык ученика — строка переключателя и списка «Мои языки». */
export interface UserLanguageView {
  readonly language: LanguageCode;
  readonly currentLevel: CurriculumLevel;
  readonly estimatedLevel: number;
  /** Входной тест этого языка пройден. */
  readonly placementDone: boolean;
  readonly completedLessons: number;
  readonly pocketSize: number;
  /** Программа уроков для языка уже есть (`hasCurriculum`). */
  readonly hasCurriculum: boolean;
  readonly addedAt: string;
}

export interface LanguagesView {
  readonly uiLanguage: LanguageCode;
  readonly activeLanguage: LanguageCode;
  /** В порядке добавления; активный язык всегда среди них. */
  readonly languages: readonly UserLanguageView[];
}

export const AddLanguageSchema = z.object({ language: LanguageCodeSchema });
export type AddLanguage = z.infer<typeof AddLanguageSchema>;

export const SetActiveLanguageSchema = z.object({ language: LanguageCodeSchema });
export type SetActiveLanguage = z.infer<typeof SetActiveLanguageSchema>;

/** Интерфейс — только на языках со словарём интерфейса (`UI_LANGUAGES`). */
export const SetUiLanguageSchema = z.object({ uiLanguage: UiLanguageCodeSchema });
export type SetUiLanguage = z.infer<typeof SetUiLanguageSchema>;
